<script setup lang="ts">
import { type Dayjs } from 'dayjs';

const { $dayjs } = useNuxtApp();

const props = withDefaults(
  defineProps<{ dateFrom: Dayjs | null; dateTo: Dayjs | null }>(),
  {
    dateFrom: null,
    dateTo: null,
  },
);

const dateFrom = computed(() => props.dateFrom);
const dateTo = computed(() => props.dateTo);

const highlightLastWeek = ref<boolean>(false);
const highlightLastTwoWeeks = ref<boolean>(false);
const highlightLastMonth = ref<boolean>(false);

const emits = defineEmits<{
  (e: 'date-range-change', range: { startDate: Dayjs; endDate: Dayjs }): void;
}>();

const handleDateRange = (rangeType: 7 | 14 | 30) => {
  const today = $dayjs();
  const endDate = today.endOf('day');
  const startDate =
    rangeType === 30
      ? today.startOf('day').subtract(1, 'month')
      : today.startOf('day').subtract(rangeType, 'day');

  emits('date-range-change', { startDate, endDate });
};

const checkDateRange = (dateFrom: Dayjs | null, dateTo: Dayjs | null): void => {
  if (!dateFrom || !dateTo) {
    highlightLastWeek.value = false;
    highlightLastTwoWeeks.value = false;
    highlightLastMonth.value = false;
    return;
  }

  const startDate = $dayjs(dateFrom).startOf('day');
  const fromDate = $dayjs(dateTo).startOf('day');

  const dayDiff = startDate.diff(fromDate, 'day');
  const weekDiff = startDate.diff(fromDate, 'week');
  const monthDiff = startDate.diff(fromDate, 'month');

  highlightLastWeek.value = weekDiff === 1 && dayDiff === 7;
  highlightLastTwoWeeks.value = weekDiff === 2 && dayDiff === 14;
  highlightLastMonth.value = monthDiff === 1;
};

watch(
  () => [dateFrom.value, dateTo.value],
  ([newDateTo, newDateFrom]) => checkDateRange(newDateFrom, newDateTo),
);

onMounted(() => checkDateRange(dateTo.value, dateFrom.value));
</script>

<template>
  <div class="flex justify-between items-center mt-2 gap-2">
    <DesignSystemButton
      type="secondary"
      padding="wide-short"
      rounding="md"
      class="font-normal text-xs w-full justify-center capitalize"
      :class="{
        'bg-primary-blue-gradient !text-light-50': highlightLastWeek,
      }"
      @click="handleDateRange(7)"
    >
      <span class="text-ellipsis truncate">{{ $t('last-week') }}</span>
    </DesignSystemButton>
    <DesignSystemButton
      type="secondary"
      padding="wide-short"
      rounding="md"
      class="font-normal text-xs w-full justify-center capitalize"
      :class="{
        'bg-primary-blue-gradient !text-light-50': highlightLastTwoWeeks,
      }"
      @click="handleDateRange(14)"
    >
      <span class="text-ellipsis truncate">{{ $t('last-fourteen-days') }}</span>
    </DesignSystemButton>
    <DesignSystemButton
      type="secondary"
      padding="wide-short"
      rounding="md"
      class="font-normal text-xs w-full justify-center capitalize"
      :class="{
        'bg-primary-blue-gradient !text-light-50': highlightLastMonth,
      }"
      @click="handleDateRange(30)"
    >
      <span class="text-ellipsis truncate">{{ $t('last-thirty-days') }}</span>
    </DesignSystemButton>
  </div>
</template>
